import { useState } from 'react';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle
} from '@headlessui/react';
import { Input } from '../Input/Input';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { logout } from '../../../store/slices/authSlice';
import { Button } from '../Button/Button';
import { XCloseModal } from '../../../icons/SvgIcons';
import { authService } from '../../../services/AuthService';
import { deleteCurrentUser } from '../../../services/UserService';
import { SecondaryButton, WhiteButton } from '../Button/Buttons';

type ErrorWithResponse = {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
};

export default function ModalDeleteAccount() {
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState('');
  const [buttonText, setButtonText] = useState('Delete Account');

  const { user } = useAppSelector((state) => state.auth);

  const dispatch = useAppDispatch();

  const handleDelete = async () => {
    if (confirmDelete === 'delete my account') {
      try {
        setButtonText('Deleting...');

        await deleteCurrentUser(confirmDelete);
        await authService.logout();
        dispatch(logout());
      } catch (error) {
        const apiError = error as ErrorWithResponse;
        const isLastAdminDeleteError =
          apiError.response?.status === 400 &&
          apiError.response?.data?.message ===
            'Cannot delete the last admin of the platform';

        if (isLastAdminDeleteError) {
          setOpen(false);
        } else {
          console.error('Logout failed:', error);
          dispatch(logout());
        }
      } finally {
        setButtonText('Delete Account');
      }
    }
  };

  return (
    <div>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center justify-center w-[111px] py-[7px] px-[11px] h-[30px] text-white text-xs font-medium bg-rose-500 rounded-full shadow-sm hover:bg-rose-600 focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-rose-600  outline-none"
      >
        Delete Account
      </button>
      <Dialog open={open} onClose={() => {}} className="relative z-10">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-[rgba(24,24,26,0.4)] transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="flex flex-col gap-6 relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all p-6 data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="bg-white">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left">
                    <div className="flex items-center justify-between">
                      <DialogTitle
                        as="h3"
                        className="text-lg leading-6 font-medium text-gray-900"
                      >
                        Delete account
                      </DialogTitle>
                      <Button buttonClassName="" onClick={() => setOpen(false)}>
                        {XCloseModal()}
                      </Button>
                    </div>
                    <div className="flex flex-col gap-6 mt-2 text-sm text-gray-500">
                      <p>
                        <span className="font-semibold">
                          Are you sure you want to delete your EU AI Audit
                          account?{' '}
                        </span>
                        Deleting your {user?.email} account will permanently
                        remove your personal data, including risk assessments,
                        reports, and shared content. This action cannot be
                        undone.
                      </p>
                      <p>
                        Any AI systems currently assigned to your account must
                        have an owner to maintain compliance continuity. Before
                        deleting your account, you can{' '}
                        <span className="font-semibold">
                          transfer ownership
                        </span>{' '}
                        of your AI systems to another account from the AI
                        Systems Overview page. Otherwise, ownership will be
                        automatically transferred to the Administration team.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <Input
                id="deleteAccount"
                label="To confirm, please type “delete my account” below."
                maxLength={17}
                onChange={(e) => setConfirmDelete(e.target.value)}
                value={confirmDelete}
                containerClassName="flex flex-col items-start gap-1 self-stretch"
                labelClassName="label"
                inputClassName="input"
              />
              <div className="flex flex-row-reverse gap-3">
                <SecondaryButton
                  onClick={handleDelete}
                  disabled={confirmDelete !== 'delete my account'}
                >
                  {buttonText}
                </SecondaryButton>
                <WhiteButton onClick={() => setOpen(false)}>Cancel</WhiteButton>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
